/**
 * useCreditStatements
 * Fetches monthly statements list for a credit + manages an inline
 * viewer state (modal with iframe). Falls back to opening in a new tab
 * when the modal is dismissed.
 *
 * Endpoint: GET /credit/backoffice/credits/{creditId}/statements
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getCreditStatements,
  getCreditStatementPdf,
} from '@/services/credit-engine.service';
import type { CreditStatementListItem } from '@/types/credit.types';

export function useCreditStatements(creditId: string) {
  const [viewerState, setViewerState] = useState<{
    isOpen: boolean;
    period: string;
    url: string | null;
    isLoading: boolean;
    statementId: string | null;
  }>({
    isOpen: false,
    period: '',
    url: null,
    isLoading: false,
    statementId: null,
  });

  const query = useQuery({
    queryKey: ['credit', 'statements', creditId],
    queryFn: () => getCreditStatements(creditId),
    enabled: Boolean(creditId),
  });

  const handleView = async (item: CreditStatementListItem) => {
    setViewerState({
      isOpen: true,
      period: item.period,
      url: null,
      isLoading: true,
      statementId: item.statement_id,
    });
    try {
      const result = await getCreditStatementPdf(item.statement_id);
      const url = result.data?.url ?? null;
      setViewerState((prev) => ({ ...prev, url, isLoading: false }));
    } catch (error) {
      console.error('Error fetching statement PDF', error);
      setViewerState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const closeViewer = () => {
    setViewerState({ isOpen: false, period: '', url: null, isLoading: false, statementId: null });
  };

  return {
    statements: query.data?.data?.statements ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    viewer: viewerState,
    handleView,
    closeViewer,
  };
}
